import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { FeatureFlagStore } from "./feature-flags.store";
import { FeatureFlagsConfig } from "./feature-flags.config";

@Component({
    selector: "app-feature-flags-panel",
    templateUrl: "./feature-flags-panel.component.html",
    styleUrls: ["./feature-flags-panel.component.css"],
})
export class FeatureFlagsPanelComponent implements OnInit {
    @Input() isFullAdmin: boolean | null = false;

    private backupFeatureFlags: Record<string, boolean> = { ...FeatureFlagStore.initialFeatureFlagsSnapshot };
    private cachedModifiedFlags: Record<string, boolean> = {};
    private lastBackupSnapshot = '';
    private lastRuntimeSnapshot = '';

    protected readonly CONFLUENCE_DOC_URL = 'https://confluence.amer.thermo.com/display/GENEAI/Gene.AI+Engineering+-+Gene.AI+Dev+Admin+Console';

    featureFlags: Record<string, boolean> = FeatureFlagStore.runtimeFeatureFlags;

    selectedFlagCategory: 'grouped' | 'all' = 'grouped';
    statusFilter: 'all' | 'active' | 'inactive' = 'all';

    unmappedGroupedFeatureFlags: string[] = [];
    groupedFeatureFlagsMapping = FeatureFlagsConfig.groupedFeatureFlagsMapping;
    groupFlagHelpMapping = FeatureFlagsConfig.groupFlagHelpMapping;
    flagHelpMapping = FeatureFlagsConfig.flagHelpMapping;

    flagSearchTerm = '';

    constructor(
        private cdRef: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.selectedFlagCategory = this.getSelectedFlagCategory();
        this.unmappedGroupedFeatureFlags = Object.keys(this.featureFlags)
            .filter((key) => !Object.values(FeatureFlagsConfig.groupedFeatureFlagsMapping).flat().includes(key))
            .sort((a, b) => a.localeCompare(b));
    }

    getSelectedFlagCategory(): 'grouped' | 'all' {
        if (!this.isFullAdmin) {
            return 'grouped';
        }

        return FeatureFlagStore.loadSelectedFlagCategory() as 'grouped' | 'all';
    }

    filterByStatus(status: 'active' | 'inactive'): void {
        if (status === 'active') {
            this.statusFilter = (this.statusFilter === 'active') ? 'all' : 'active';
        } else if (status === 'inactive') {
            this.statusFilter = (this.statusFilter === 'inactive') ? 'all' : 'inactive';
        }

        this.updateFilteredMappedGroupMapping();
        this.updatedFilteredUnmappedGroupMapping();
    }

    updateFilteredMappedGroupMapping(): void {
        const groupMapping = Object.entries(FeatureFlagsConfig.groupedFeatureFlagsMapping)
            .reduce((acc, [group, flags]) => {
                if (this.statusFilter === 'all') {
                    acc[group] = flags;
                } else {
                    acc[group] = flags.filter(flag => this.featureFlags[flag] === (this.statusFilter === 'active'));
                }

                return acc;
            }, {} as Record<string, string[]>);

        this.groupedFeatureFlagsMapping = groupMapping;
    }

    updatedFilteredUnmappedGroupMapping(): void {
        this.unmappedGroupedFeatureFlags = Object.keys(this.featureFlags)
            .filter((key) => !Object.values(FeatureFlagsConfig.groupedFeatureFlagsMapping).flat().includes(key))
            .filter((key) => {
                if (this.statusFilter === 'active') {
                    return this.featureFlags[key] === true;
                } else if (this.statusFilter === 'inactive') {
                    return this.featureFlags[key] === false;
                }

                return true;
            })
            .sort((a, b) => a.localeCompare(b));
    }

    get areFlagsUnchanged(): boolean {
        return Object.keys(this.featureFlags).every(
            (key) => this.featureFlags[key] === this.backupFeatureFlags[key]
        );
    }

    reset(): void {
        if (this.areFlagsUnchanged) {
            return;
        }

        Object.keys(this.featureFlags).forEach(key => {
            this.featureFlags[key] = this.backupFeatureFlags[key];
        });

        this.updateFilteredMappedGroupMapping();
        this.updatedFilteredUnmappedGroupMapping();

        FeatureFlagStore.clearFeatureFlagsFromStorage();

        this.cdRef.detectChanges();
    }

    trackByKey(index: number, key: string): string {
        return key;
    }

    toggleFlag(key: string): void {
        this.featureFlags[key] = !this.featureFlags[key];

        FeatureFlagStore.saveToLocalStorage(this.featureFlags);

        this.updateFilteredMappedGroupMapping();
        this.updatedFilteredUnmappedGroupMapping();

        this.cdRef.detectChanges();
    }

    toggleGroup(group: string): void {
        const isEnabled = FeatureFlagsConfig.groupedFeatureFlagsMapping[group]
            .filter(flag => FeatureFlagStore.isFlagAvailable(flag))
            .every(flag => this.featureFlags[flag as keyof typeof this.featureFlags]);

        FeatureFlagsConfig.groupedFeatureFlagsMapping[group]
            .filter(flag => FeatureFlagStore.isFlagAvailable(flag))
            .forEach(flag => this.featureFlags[flag as keyof typeof this.featureFlags] = !isEnabled);

        FeatureFlagStore.saveToLocalStorage(this.featureFlags);

        this.updateFilteredMappedGroupMapping();
        this.updatedFilteredUnmappedGroupMapping();

        this.cdRef.detectChanges();
    }

    isActiveFlag(key: string): boolean {
        return this.featureFlags[key] === true;
    }

    isFlagModified(key: string): boolean {
        return this.featureFlags[key] !== this.backupFeatureFlags[key];
    }

    getModifiedFlags(): Record<string, boolean> {
        const currentBackup = JSON.stringify(this.backupFeatureFlags);
        const currentRuntime = JSON.stringify(this.featureFlags);

        if (this.lastBackupSnapshot !== currentBackup || this.lastRuntimeSnapshot !== currentRuntime) {
            this.lastBackupSnapshot = currentBackup;
            this.lastRuntimeSnapshot = currentRuntime;

            const modified: Record<string, boolean> = {};
            Object.keys(this.featureFlags).forEach((key) => {
                modified[key] = this.featureFlags[key] !== this.backupFeatureFlags[key];
            });
            this.cachedModifiedFlags = modified;
        }

        return this.cachedModifiedFlags;
    }

    flagByStatusCount(status: 'active' | 'inactive'): number {
        const availableFlags = Object.keys(this.featureFlags).filter(key => FeatureFlagStore.isFlagAvailable(key));

        if (status === 'active') {
            return availableFlags.filter(key => this.featureFlags[key]).length;
        } else if (status === 'inactive') {
            return availableFlags.filter(key => !this.featureFlags[key]).length;
        }

        return 0;
    }

    displayAllFlags(): void {
        this.selectedFlagCategory = 'all';
        FeatureFlagStore.saveSelectedFlagCategory(this.selectedFlagCategory);
    }

    displayGroupedFlags(): void {
        this.selectedFlagCategory = 'grouped';
        FeatureFlagStore.saveSelectedFlagCategory(this.selectedFlagCategory);
    }

    get filteredAllFlagsFirstColumn(): string[] {
        return this.filteredAllFlags.slice(0, Math.ceil(this.filteredAllFlags.length / 2));
    }

    get filteredAllFlagsSecondColumn(): string[] {
        return this.filteredAllFlags.slice(Math.ceil(this.filteredAllFlags.length / 2));
    }


    get filteredAllFlags(): string[] {
        return Object.keys(this.featureFlags)
            .filter((key: string) => {
                const matchesStatus =
                    this.statusFilter === 'all' ||
                    (this.statusFilter === 'active' && this.featureFlags[key]) ||
                    (this.statusFilter === 'inactive' && !this.featureFlags[key]);

                const matchesSearch =
                    this.flagSearchTerm === '' ||
                    key.toLowerCase().includes(this.flagSearchTerm.toLowerCase());

                return matchesStatus && matchesSearch;
            })
            .sort((a, b) => a.localeCompare(b));
    }

    get filteredGroupedFeatureFlagsMapping(): Record<string, string[]> {
        if (!this.flagSearchTerm) return this.groupedFeatureFlagsMapping;
        const search = this.flagSearchTerm.toLowerCase();
        return Object.fromEntries(
            Object.entries(this.groupedFeatureFlagsMapping).map(([group, flags]) => [
                group,
                flags.filter(flag => flag.toLowerCase().includes(search))
            ])
        ) as Record<string, string[]>;
    }

    get filteredUnmappedGroupedFeatureFlags(): string[] {
        if (!this.flagSearchTerm) return this.unmappedGroupedFeatureFlags;
        const search = this.flagSearchTerm.toLowerCase();
        return this.unmappedGroupedFeatureFlags.filter(flag =>
            flag.toLowerCase().includes(search)
        );
    }
}
